// biome-ignore lint/style/useNodejsImportProtocol: we are on the browser side

import { RpcSession, type RpcStub } from 'capnweb';
import { EventEmitter } from 'events';

import * as api from '@gitbook/api-client';
import { getHiveEndpoint } from '@gitbook/env';
import { WebSocket, WebSocketState } from '@gitbook/util-websocket';
import {
    type AgentRpc,
    createSocketTransport,
    type RpcSocketTransport,
} from '@services/content-agents/browser';

export enum GitBookAgentClientStatus {
    CONNECTING = 'connecting',
    IDLE = 'idle',
    PROCESSING = 'processing',
    ERRORED = 'errored',
}

export type GitBookAgentClientState = {
    status: GitBookAgentClientStatus;
    suggestions: string[];
    messages: api.AIMessage[];
};

export type GitBookAgentClientOptions = {
    pathname: string;

    /**
     * Get a token to authenticate to the agent.
     */
    getToken: () => Promise<string>;
};

/**
 * Client for a content AI agent.
 */
export class GitBookAgentClient<Rpc extends {} = any> {
    private ws: WebSocket;
    private events = new EventEmitter();
    private rpcTransport: RpcSocketTransport;

    // @ts-expect-error - Serializable is not exported from capnweb
    private rpcSession: RpcSession<Rpc & AgentRpc>;
    // @ts-expect-error - Serializable is not exported from capnweb
    public rpc: RpcStub<Rpc & AgentRpc>;

    private agentState: api.AgentState = {
        status: api.AgentStatus.Idle,
        suggestions: [],
    };

    public state: GitBookAgentClientState = {
        status: GitBookAgentClientStatus.CONNECTING,
        suggestions: [],
        messages: [],
    };

    constructor(params: GitBookAgentClientOptions) {
        this.ws = new WebSocket(async () => {
            const url = new URL(params.pathname, getHiveEndpoint());
            url.protocol = url.protocol.replace('http', 'ws');

            const token = await params.getToken();
            url.searchParams.set('token', token);

            return url;
        });

        this.rpcTransport = createSocketTransport(this.ws);
        // @ts-expect-error - Serializable is not exported from capnweb
        this.rpcSession = new RpcSession<Rpc>(this.rpcTransport);
        this.rpc = this.rpcSession.getRemoteMain();

        this.ws.listen('state', () => {
            this.updateState((state) => ({
                ...state,
                status: getStatus(this.ws, this.agentState),
            }));
        });

        this.ws.listen('message', (rawMessage) => {
            if (typeof rawMessage !== 'string') {
                return;
            }

            const data = JSON.parse(rawMessage) as api.AgentStream;

            switch (data.type) {
                /**
                 * The agent is responding to a RPC call.
                 */
                case 'rpc': {
                    this.rpcTransport.onMessage(
                        typeof data.message === 'string'
                            ? data.message
                            : JSON.stringify(data.message)
                    );
                    break;
                }

                /**
                 * The agent is broadcasting new messages.
                 */
                case 'messages': {
                    this.updateState((state) => {
                        if (data.operation === 'replace') {
                            state.messages = data.messages;
                        } else {
                            state.messages = [...state.messages];
                            data.messages.forEach((message) => {
                                upsertMessage(state.messages, message);
                            });
                        }

                        return state;
                    });
                    break;
                }

                case 'message_response': {
                    const response = data.response;
                    switch (response.type) {
                        /**
                         * The agent is starting a new response.
                         */
                        case 'response_start': {
                            this.updateProcessingMessage(response.messageId, (message) => {
                                message.task = response.task;
                            });
                            break;
                        }

                        /**
                         * The agent is processing a tool call in a new message.
                         */
                        case 'response_tool_call': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    step.toolCalls ??= [];
                                    step.toolCalls.push(response.toolCall);
                                }
                            );
                            break;
                        }

                        /**
                         * The agent is writing the content of a new message.
                         */
                        case 'response_reasoning':
                        case 'response_document': {
                            this.updateProcessingMessageStep(
                                response.messageId,
                                response.stepIndex,
                                (step) => {
                                    const container =
                                        response.type === 'response_reasoning'
                                            ? 'reasoning'
                                            : 'content';

                                    step[container] ??= {
                                        object: 'document',
                                        data: {},
                                        nodes: [],
                                    };
                                    step[container] = {
                                        ...step[container],
                                        nodes: [...step[container].nodes],
                                    };
                                    if (response.operation === 'insert') {
                                        step[container].nodes.push(...response.blocks);
                                    } else {
                                        step[container].nodes.splice(
                                            -response.blocks.length,
                                            response.blocks.length,
                                            ...response.blocks
                                        );
                                    }
                                }
                            );
                            break;
                        }
                    }
                    break;
                }

                /**
                 * The state is being updated
                 */
                case 'state': {
                    this.agentState = data.state;
                    this.updateState((state) => ({
                        ...state,
                        status: getStatus(this.ws, data.state),
                        suggestions: data.state.suggestions ?? [],
                    }));
                    break;
                }
            }
        });
    }

    /**
     * Connect to the agent.
     */
    public connect() {
        this.ws.open();
    }

    /**
     * Disconnect from the agent.
     */
    public disconnect() {
        this.ws.destroy();
    }

    /**
     * Listen to events from the agent.
     */
    public listen(event: 'state', callback: (event: GitBookAgentClientState) => void): () => void {
        this.events.on(event, callback);

        return () => {
            this.events.off(event, callback);
        };
    }

    /**
     * Post a message to the agent.
     */
    public postMessage(message: api.AIMessageInput) {
        this.rpc.postMessage(message);
    }

    /**
     * Reset the conversation with the agent.
     */
    public async reset() {
        await this.rpc.reset();
    }

    /**
     * Update a message in the chat.
     */
    private updateProcessingMessage(messageId: string, callback: (message: api.AIMessage) => void) {
        this.updateState((state) => {
            state.messages = [...state.messages];
            const existing = state.messages.findIndex((m) => m.id === messageId);
            if (existing !== -1) {
                state.messages[existing] = { ...state.messages[existing] };
                callback(state.messages[existing]);
            } else {
                const newMessage: api.AIMessage = {
                    id: messageId,
                    role: api.AIMessageRole.Assistant,
                    steps: [],
                };
                callback(newMessage);
                state.messages.push(newMessage);
            }

            return state;
        });
    }

    /**
     * Update a step in a message.
     */
    private updateProcessingMessageStep(
        messageId: string,
        stepIndex: number,
        callback: (step: api.AIMessageStep) => void
    ) {
        this.updateProcessingMessage(messageId, (message) => {
            if (stepIndex > message.steps.length) {
                throw new Error(
                    `Step index out of bounds ${stepIndex} for message ${messageId} (${message.steps.length} steps)`
                );
            }

            if (message.steps[stepIndex]) {
                message.steps = [...message.steps];
                message.steps[stepIndex] = { ...message.steps[stepIndex] };
                callback(message.steps[stepIndex]);
            } else {
                message.steps = [
                    ...message.steps,
                    {
                        content: {
                            object: 'document',
                            data: {},
                            nodes: [],
                        },
                    },
                ];
                callback(message.steps[stepIndex]);
            }
        });
    }

    /**
     * Update the agent state.
     */
    private updateState(fn: (previous: GitBookAgentClientState) => GitBookAgentClientState) {
        this.state = fn({
            ...this.state,
        });
        this.events.emit('state', this.state);
    }
}

/**
 * Utility to extract the latest task from the state of the agent.
 */
export function extractLatestAgentTask(state: GitBookAgentClientState): api.AIMessageTask | null {
    const latestMessage = state.messages[state.messages.length - 1];
    if (!latestMessage) {
        return null;
    }

    return latestMessage.task ?? null;
}

/**
 * Utility to extract the active task from the state of the agent.
 */
export function extractActiveAgentTask(state: GitBookAgentClientState): api.AIMessageTask | null {
    if (state.status !== GitBookAgentClientStatus.PROCESSING) {
        return null;
    }

    return extractLatestAgentTask(state);
}

function upsertMessage(messages: api.AIMessage[], message: api.AIMessage) {
    const existing = messages.findIndex((m) => m.id === message.id);
    if (existing !== -1) {
        messages[existing] = message;
    } else {
        messages.push(message);
    }
}

function getStatus(ws: WebSocket, state: api.AgentState): GitBookAgentClientStatus {
    if (ws.state === WebSocketState.CLOSED) {
        return GitBookAgentClientStatus.ERRORED;
    }

    if (ws.state === WebSocketState.CONNECTING) {
        return GitBookAgentClientStatus.CONNECTING;
    }

    if (state.status === api.AgentStatus.Processing) {
        return GitBookAgentClientStatus.PROCESSING;
    }

    if (state.status === api.AgentStatus.Errored) {
        return GitBookAgentClientStatus.ERRORED;
    }

    return GitBookAgentClientStatus.IDLE;
}
